import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useLazyQuery } from "@stadium/services/apollo-client";

import groupBy from "lodash/groupBy";
import sumBy from "lodash/sumBy";

import { getStoreCurrencyCode } from "@selectors/store";
import { getCountry } from "@selectors/country";

import { useStoreContext } from "@components/store/provider";

import { getBundleProductsData } from "@helpers/orders/cart";
import { getCommonGlobalCountryVariables } from "@helpers/store";

import { fetchKitShopApi } from "@graphql/queries/kit/fetch-kit-shop";

import { useFetchInventoryDetails } from "@hooks/product-detail/use-fetch-inventory";

import { GLOBAL_COUNTRY } from "@constants/country";
import { useIsAuthGraphQLReady } from "@stadium/services/auth/client";

const defaultArr = [];
const defaultObj = {};

const useFetchKitItems = ({ skipInventoryFetching = false, fetchKit = true } = {}) => {
  const selectedCountry = useSelector(getCountry);
  const storeCurrencyCode = useSelector(getStoreCurrencyCode);

  const [bundleProducts, setBundleProducts] = useState(defaultObj);

  const { storeNumber } = useStoreContext();

  const [fetchKitShop, { data, loading }] = useLazyQuery(fetchKitShopApi);

  const isGlobalCountry = GLOBAL_COUNTRY.iso === selectedCountry?.iso;
  const isAuthGraphQLReady = useIsAuthGraphQLReady();
  const commonGlobalCountryVariables = getCommonGlobalCountryVariables({
    country: selectedCountry,
    isGlobalCountry,
  });

  const { fetchInventoryDetails, variantData, isInventoryFetching, rawResponse } =
    useFetchInventoryDetails();

  useEffect(() => {
    if (storeNumber && storeCurrencyCode && !loading && fetchKit && isAuthGraphQLReady) {
      fetchKitShop({
        variables: {
          storeNumber,
          currency: storeCurrencyCode,
          ...commonGlobalCountryVariables,
        },
      });
    }
  }, [storeNumber, loading, storeCurrencyCode, fetchKit, isAuthGraphQLReady]);

  const bundleShop = data?.bundleShop || defaultObj;
  const bundles = bundleShop.bundles || defaultArr;
  const kitZonesMapping = useMemo(() => groupBy(bundles, "bundleZone"), [bundles]);

  const lineItems = useMemo(
    () =>
      bundles.reduce((totalLineItems, { bundleProducts }) => {
        totalLineItems.push(...bundleProducts);
        return totalLineItems;
      }, []),
    [bundles],
  );

  useEffect(() => {
    if (!skipInventoryFetching && lineItems.length && !isInventoryFetching) {
      const products = lineItems.map(({ product }) => product);
      fetchInventoryDetails(products);
    }
  }, [lineItems, skipInventoryFetching]);

  useEffect(() => {
    const productsMapping = getBundleProductsData({
      lineItems,
      variantData,
    });

    setBundleProducts(productsMapping);
  }, [lineItems, variantData]);

  const totalProductCount = useMemo(() => sumBy(lineItems, "quantity"), [lineItems]);

  const value = useMemo(
    () => ({
      kitZonesMapping,
      totalProductCount,
      loading,
      lineItems,
      variantData,
      inventoryData: rawResponse,
      bundleProducts,
      bundleShop,
      isInventoryFetching,
    }),
    [lineItems, bundleProducts, loading, variantData, isInventoryFetching],
  );

  return value;
};

export default useFetchKitItems;
